<template>
  <div class="accordion">
    <div class="wrapper" :class="state.isOpen ? 'open' : 'closed'">
      <div
        class="accordion-header h5"
        :aria-label="`${state.isOpen ? 'close' : 'open'} accordion`"
        :aria-expanded="state.isOpen ? 'true' : 'false'"
        role="button"
        @click="toggleAccordion"
      >
        {{ title }}
      </div>
      <div class="content-panel">
        <div class="accordion-desc">
          <slot name="content">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="dynamic-content" v-html="desc" />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const emit = defineEmits(['' as string]);

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  emitOpen: {
    type: String,
    default: null,
  },
  desc: {
    type: String,
    required: true,
  },
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  clickStop: {
    type: Boolean,
    default: false,
  },
});

const state = reactive({
  isOpen: props.defaultOpen,
});

watch(
  () => props.defaultOpen,
  (newValue: boolean) => {
    state.isOpen = newValue;
  }
);

function toggleAccordion(e: Event) {
  if (props.clickStop) {
    const target = e.target as HTMLElement;
    if (target && !target.classList.contains('accordion-header')) return;
  }
  state.isOpen = !state.isOpen;
  if (props.emitOpen) emit(props.emitOpen, state.isOpen);
}
</script>

<style lang="scss">
.accordion .dynamic-content {
  img {
    height: 64px !important; // override CTS inline style
    width: 64px !important;
  }
  li {
    @include local-mixins.p;
    /* stylelint-disable-next-line no-duplicate-selectors */
    & {
      margin-bottom: 0;
    }
  }
  li,
  p,
  a {
    font-size: 0.875rem;
  }

  .dropdown-product {
    display: grid;
    grid-template-areas:
      'image title'
      'image desc';
    grid-template-columns: 74px auto; // 10px right
    color: $color-neutral-cool-900;

    img {
      grid-area: image;

      + p {
        grid-area: title;
        margin-top: 5px;
        margin-bottom: 5px;

        + p {
          grid-area: desc;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
$content-transition: all 0.5s ease-out;
$icon-transition: transform 0.5s ease-out;

.accordion {
  box-sizing: border-box;
  width: 100%;
  margin: 0;

  + .accordion {
    .wrapper {
      border-top: none;
    }
  }
}

.accordion-desc {
  font-size: 1rem;
  line-height: 150%;
  color: $color-neutral-warm-900;
}

.content-panel {
  text-align: left;
  background-color: $color-neutral-white;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: $content-transition;
}

.accordion-header {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  background-color: $color-neutral-white;
  transition: margin-bottom 0.25s ease-out;
  padding: 1.5rem 0 1.25rem 0;

  &::after {
    content: '';
    display: block;
    margin-left: auto;
    transform: rotate(0deg);
    transition: $icon-transition;

    @include local-mixins.backgroundMaskImage(
      local-functions.svg-img-source('arrow-down-s-line.svg'),
      $color-neutral-black,
      40px,
      40px
    );
  }
}

.wrapper {
  border-top: 1px solid $color-neutral-cool-200;
  border-bottom: 1px solid $color-neutral-cool-200;

  &.open {
    .accordion-header {
      margin-bottom: 1.25rem;

      &::after {
        transform: rotate(-180deg);
        transition: $icon-transition;
      }
    }

    .content-panel {
      transition: $content-transition;
      overflow: visible;
      opacity: 1;
      max-height: 1200px;
      padding-bottom: 1.25rem;
    }
  }
}
</style>
